import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  changeField,
  initializeForm,
  resetPassword,
  checkResetPasswordToken,
  resetPasswordConfirm,
  changePassword,
} from '../../modules/auth';
import { validate } from '../../lib/api/auth';
import PasswordAssistanceForm from '../../components/auth/PasswordAssistanceForm';
import PasswordAssistanceForm2 from '../../components/auth/PasswordAssistanceForm2';
import ChangePassword from '../../components/auth/ChangePassword';
import MessageBox from '../../components/common/MessageBox';
import { setchangePasswordSuccess, changePasswordSuccess } from '../../modules/auth';

const PasswordAssistanceContainer = ({ match, history }) => {
  const { t } = useTranslation();
  const { token } = match.params;
  const [error, setError] = useState(null);
  const [tokenValid, setTokenValid] = useState(false);

  const dispatch = useDispatch();
  const { form, auth, authError, errorMessage, user } = useSelector(
    ({ auth, user, app }) => ({
      form: auth.signup,
      auth: auth.auth,
      authError: auth.authError,
      errorMessage: auth.error,
      user: user.user,
    }),
  );

  const [messageVisible, setMessageVisible] = useState(false);
  const [messageTitle, setMessageTitle] = useState('');
  const [messageType, setMessageType] = useState('ok');
  const [messageContent, setMessageContent] = useState('');

  const isLoadingRef = useRef(false);

  const onChange = (e) => {
    const { value, name } = e.target;
    dispatch(
      changeField({
        form: 'signup',
        key: name,
        value,
      }),
    );
  };

  const onFocusOut = (e) => {
    const { value, name } = e.target;
    validate_password(value, 'password', name);
  };

  const validate_password = async (value, formName, field) => {
    if (field === 'old_password') return;
    const formData = new FormData();
    formData.append('old_password', '');
    formData.append('new_password', '');
    formData.append('new_password_confirm', '');
    if (field === 'password') {
      field = 'new_password';
    }
    if (field === 'passwordConfirm') {
      field = 'new_password_confirm';
      formData.set('new_password', form.password);
    }
    if (field) formData.set(field, value);

    try {
      let res = await validate({ formData, form: formName, field });

      if (res.statusText !== 'OK') {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const node = document.getElementsByName(field + 'Validate')[0];
      if (JSON.stringify(res.data) === '{}') {
        if (node.childNodes.length > 0) node.removeChild(node.childNodes[0]);
        node.classList.remove('validateCss');
      } else {
        if (res.data.error_message) {
          let text = '';
          res.data.error_message.forEach((el) => {
            text += '<div key={index}>' + t(el) + '</div>';
          });

          let small = document.createElement('small');
          // small.setAttribute('class', 'message');
          small.innerHTML = "<font color='red'>" + `${text}` + '</font>';

          if (node.childNodes.length === 0) node.appendChild(small);
          else node.replaceChild(small, node.childNodes[0]);
          node.classList.add('validateCss');
        }
      }
    } catch (e) {
      console.log(e);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();

    if (user || token) {
      //Set passwoard
      const { old_password, password, passwordConfirm } = form;
      if (old_password) formData.append('old_password', old_password);
      if (password) formData.append('new_password', password);
      if (passwordConfirm) formData.append('new_password_confirm', passwordConfirm);

      if (password === passwordConfirm) {
        if (user) dispatch(changePassword({ formData }));
        else {
          if (isLoadingRef.current) {
            return;
          }
          isLoadingRef.current = true;

          await dispatch(resetPasswordConfirm({ token, password }));

          isLoadingRef.current = false;
        }
      } else {
        setError([t('The two password fields didn’t match')]);
      }
    } else {
      const { username } = form;

      if (!username) {
        setError(t('Email is required'));
        return;
      }

      setError(null);
      isLoadingRef.current = true;

      dispatch(resetPassword({ email: username }));
    }
  };

  const onBackButtonEvent = (e) => {
    e.preventDefault();
    history.push('/');
  };

  const onConfirmBoxOk = () => {
    setMessageVisible(false);
    setchangePasswordSuccess(false);
    history.push('/');
  };

  const onCancel = () => {
    setMessageVisible(false);
  };

  useEffect(() => {
    if (authError) {
      isLoadingRef.current = false;

      if (authError.response?.status === 404) {
        setError(t('This email is not registered'));
      } else if (authError.response?.data?.detail) {
        setError(t(authError.response.data.detail));
      } else {
        setError(t('An error occurred. Please try again.'));
      }
    }
  }, [authError, t]);

  useEffect(() => {
    if (auth && !authError && !user && !token) {
      isLoadingRef.current = false;
      history.push('/passwordLink');
    }
  }, [auth, authError, user, token, history]);

  useEffect(() => {
    if (changePasswordSuccess) {
      setMessageVisible(true);
      setMessageContent(t('Password has been changed successfully'));
      setMessageTitle(t('Info'));
    }
  }, [changePasswordSuccess, t]);

  useEffect(() => {
    dispatch(initializeForm('signup'));
    if (token) {
      dispatch(checkResetPasswordToken({ token }));
    }
    return () => {
      dispatch(initializeForm('authError'));
    };
  }, [dispatch, token]);

  useEffect(() => {
    try {
      if (errorMessage) {
        if (errorMessage.response?.data?.old_password) {
          if (errorMessage.response.data.old_password[0] === 'Wrong password.') {
            setError(t('The old password is incorrect'));
          } else {
            setError(errorMessage.response.data.old_password);
          }

          dispatch(changeField({ form: 'signup', key: 'old_password', value: '' }));
          dispatch(changeField({ form: 'signup', key: 'password', value: '' }));
          dispatch(changeField({ form: 'signup', key: 'passwordConfirm', value: '' }));
        }
      }
    } catch (e) {
      console.log(e);
    }
  }, [errorMessage, dispatch, t]);

  useEffect(() => {
    try {
      if (auth) {
        if (auth.status === 'OK') {
          setTokenValid(true);
        } else if (auth.detail === 'Invalid token.' || auth.status === 'notfound') {
          history.push('/tokenExpired');
        }
      }
    } catch (e) {
      console.log(e);
    }
  }, [auth, history]);

  return !user ? (
    !token ? (
      <PasswordAssistanceForm
        form={form}
        onChange={onChange}
        onSubmit={onSubmit}
        error={error}
        onBackButtonEvent={onBackButtonEvent}
        isLoadingRef={isLoadingRef}
      />
    ) : (
      tokenValid && (
        <PasswordAssistanceForm2
          form={form}
          onChange={onChange}
          onSubmit={onSubmit}
          error={error}
          onFocusOut={onFocusOut}
          isLoadingRef={isLoadingRef}
        />
      )
    )
  ) : (
    <ChangePassword
      form={form}
      onChange={onChange}
      onSubmit={onSubmit}
      error={error}
      messageVisible={messageVisible}
      messageTitle={messageTitle}
      messageType={messageType}
      messageContent={messageContent}
      onConfirmBoxOk={onConfirmBoxOk}
      onCancel={onCancel}
      onFocusOut={onFocusOut}
    />
  );
};

export default withRouter(React.memo(PasswordAssistanceContainer));
